import type {
  ActivityEntry,
  BudgetMember,
  BudgetSpace,
  Category,
  ConsumptionData,
  DebtDetails,
  RecurringTemplate,
  Subcategory,
  Transaction,
  User,
} from '../../domain/model';
import { spendingRule, type TransactionType } from '../../domain/model';
import { bool, json, type EntitySpec } from './entity';

/** Field ↔ column maps. Adding a field means: migration + one line here. */

export const userSpec: EntitySpec<User> = {
  table: 'users',
  fields: { displayName: 'display_name', email: 'email', kind: 'kind' },
};

export const spaceSpec: EntitySpec<BudgetSpace> = {
  table: 'budget_spaces',
  spaceField: 'id',
  fields: {
    name: 'name',
    type: 'type',
    currency: 'currency',
    billingCountry: 'billing_country',
    monthlyTargetMinor: 'monthly_target_minor',
    ownerUserId: 'owner_user_id',
  },
};

export const memberSpec: EntitySpec<BudgetMember> = {
  table: 'budget_members',
  spaceField: 'spaceId',
  fields: { spaceId: 'space_id', userId: 'user_id', displayName: 'display_name', role: 'role' },
};

export const categorySpec: EntitySpec<Category> = {
  table: 'categories',
  spaceField: 'spaceId',
  fields: {
    spaceId: 'space_id',
    systemKey: 'system_key',
    name: 'name',
    kind: 'kind',
    nature: 'nature',
    colorSlot: 'color_slot',
    sortOrder: 'sort_order',
    archivedAt: 'archived_at',
  },
};

export const subcategorySpec: EntitySpec<Subcategory> = {
  table: 'subcategories',
  spaceField: 'spaceId',
  fields: {
    spaceId: 'space_id',
    categoryId: 'category_id',
    systemKey: 'system_key',
    name: 'name',
    defaultUnit: 'default_unit',
    sortOrder: 'sort_order',
    archivedAt: 'archived_at',
  },
};

/**
 * Whose it is + owner and type + spending flag go out together, and a row
 * that arrives with the halves disagreeing (the local database refuses such
 * a row, which would stop every pull) is put right: the type's fixed rule
 * wins, a shared entry has no owner, a personal one without an owner is the
 * payer's (or shared, if the payer is unknown).
 */
const PAIRS = [['ownership', 'ownerMemberId'], ['type', 'countsAsSpending']] as const;

function normalizeMoneyRow(f: Record<string, unknown>): Record<string, unknown> {
  if (typeof f.type === 'string' && 'countsAsSpending' in f) {
    const rule = spendingRule(f.type as TransactionType);
    if (rule !== 'choose') f.countsAsSpending = rule;
  }
  if (f.ownership === 'shared' && f.ownerMemberId != null) f.ownerMemberId = null;
  if (f.ownership === 'personal' && 'ownerMemberId' in f && f.ownerMemberId == null) {
    if (typeof f.paidBy === 'string') f.ownerMemberId = f.paidBy;
    else f.ownership = 'shared';
  }
  return f;
}

export const transactionSpec: EntitySpec<Transaction> = {
  table: 'transactions',
  spaceField: 'spaceId',
  together: PAIRS.map((p) => [...p]) as (keyof Transaction)[][],
  normalize: normalizeMoneyRow,
  fields: {
    spaceId: 'space_id',
    categoryId: 'category_id',
    subcategoryId: 'subcategory_id',
    type: 'type',
    amountMinor: 'amount_minor',
    currency: 'currency',
    originalAmountMinor: 'original_amount_minor',
    originalCurrency: 'original_currency',
    date: 'date',
    addedBy: 'added_by',
    paidBy: 'paid_by',
    ownership: 'ownership',
    ownerMemberId: 'owner_member_id',
    countsAsSpending: ['counts_as_spending', bool],
    notes: 'notes',
    provider: 'provider',
    dueDate: 'due_date',
    periodStart: 'period_start',
    periodEnd: 'period_end',
    recurringTemplateId: 'recurring_template_id',
  },
};

export const recurringSpec: EntitySpec<RecurringTemplate> = {
  table: 'recurring_templates',
  spaceField: 'spaceId',
  together: PAIRS.map((p) => [...p]) as (keyof RecurringTemplate)[][],
  normalize: normalizeMoneyRow,
  fields: {
    spaceId: 'space_id',
    categoryId: 'category_id',
    subcategoryId: 'subcategory_id',
    name: 'name',
    kind: 'kind',
    type: 'type',
    amountMinor: 'amount_minor',
    currency: 'currency',
    frequency: 'frequency',
    dayOfMonth: 'day_of_month',
    nextDueDate: 'next_due_date',
    paidBy: 'paid_by',
    ownership: 'ownership',
    ownerMemberId: 'owner_member_id',
    countsAsSpending: ['counts_as_spending', bool],
    active: ['active', bool],
  },
};

export const consumptionSpec: EntitySpec<ConsumptionData> = {
  table: 'consumption_data',
  spaceField: 'spaceId',
  fields: {
    spaceId: 'space_id',
    transactionId: 'transaction_id',
    quantity: 'quantity',
    unit: 'unit',
    periodStart: 'period_start',
    periodEnd: 'period_end',
  },
};

export const debtSpec: EntitySpec<DebtDetails> = {
  table: 'debt_details',
  spaceField: 'spaceId',
  fields: {
    spaceId: 'space_id',
    transactionId: 'transaction_id',
    remainingBalanceMinor: 'remaining_balance_minor',
    interestRateBp: 'interest_rate_bp',
    installmentsLeft: 'installments_left',
  },
};

export const activitySpec: EntitySpec<ActivityEntry> = {
  table: 'activity_entries',
  spaceField: 'spaceId',
  fields: {
    spaceId: 'space_id',
    actorMemberId: 'actor_member_id',
    action: 'action',
    entity: 'entity',
    entityId: 'entity_id',
    summary: ['summary', json],
    at: 'at',
  },
};
